// Copyright 2020 Drivetrain Hub LLC
// Vuex module for managing state of user interface.
// `namespaced` option applies to getters, actions, and mutations; state is always namespaced.

import handlers from "../../handlers";
import requests from "../../requests";
import {EventBus} from "../../event_bus";

const CONTEXT_MENUS = {
  ContextMenuPart: false,
  ContextMenuPart3: false,
  ContextMenuConnection: false,
  ContextMenuConnect: false,
  ContextMenuConfig: false,
  ContextMenuDutyCycle: false,
  ContextMenuLoadCase: false,
};

const CONTEXT_MENUS_MAP = {
  Part: 'ContextMenuPart',
  Part3: 'ContextMenuPart3',
  Connection: 'ContextMenuConnection',
  Connect: 'ContextMenuConnect',
  GearsConfig: 'ContextMenuConfig',
  DutyCycle: 'ContextMenuDutyCycle',
  LoadCase: 'ContextMenuLoadCase',
};

export default {
  namespaced: false,
  state: {
    loading: false,

    /** UI component visibility. */
    visible: {
      dialog: {
        // ASSEMBLY
        DialogAssmConnect: false,
        DialogAssmPosition: false,
        // EXPORT
        DialogDataAnalysis: false,
        DialogDataMfg: false,
        DialogDataModel: false,
        DialogExportCad: false,
        DialogExportStl: false,
        DialogExportFe: false,
        // LIBRARY
        DialogLibGear: false,
        DialogLibBasicRack: false,
        DialogLibHob: false,
        DialogLibShaper: false,
        // MODEL
        DialogModelInfo: false,
        DialogReleaseInfo: false,
        DialogModelShare: false,
        DialogModelFiles: false,
        // ANALYSIS
        DialogSetupConfig: false,
        DialogSetupSettings: false,
        DialogSetupDutyCycles: false,
        DialogSetupLoadCases: false,
        DialogRunPowerflow: false,
        // BUILD
        DialogBuildPrint: false,
        DialogBuildMfg: false,
      },
      contextmenu: {...CONTEXT_MENUS},
    },

    /** UI viewer properties. */
    viewer: {
      // Display loading spinner.
      loading: false,
      // Currently active tab name.  Number naming is used for dynamic handling of closable tabs.
      activeTabName: '1',
      // Initial number of tabs [Model,].  Used for closable tab naming.
      tabIndex: 1,
      // Array of closable tab objects containing properties for tab name, icon, title, content (component), and data.
      closableTabs: [],
    },

    /** UI assignment of tool, material, or quality. */
    assign: {
      hob: false,
      shaper: false,
      basicRack: false,
    },

    /** UI status message; displayed in lower right. */
    status: {
      message: {'1': ''},  // { <viewer tab name> : <message> }
      loading: false,
    },

    /** UI behavior to skip route update $confirm popup. */
    skipRouteConfirm: false,

    /** UI selections. Usage includes context menus. */
    selected: {
      evt: null,
      part: null,
      parts: [],
      connection: null,
      connections: [],
      config: null,
      dutyCycle: null,
      loadCase: null,
    },

    /** UI key presses.  Usage includes 3D viewer. */
    keydown: {
      Escape: false,
    },

    /** Items selected for editing, export, etc. */
    editPart: null,
    editConfig: null,
    exportParts: [],
    exportConns: [],
    exportDutyCycle: null,
    exportLoadCases: [],

    /** Properties corresponding to component size, useful for normalizing certain graphics. */
    size: {
      diameters: [],
      diameterMin: 0,
      crossHairLength: 0,
    },

    /** Properties for analyzing and reviewing results. */
    analyze: {
      loadCase: null,  // load case to review
      animate: false,  // 3D scene animation
      playbackSpeed: 0.001,  // 3D scene animation
      color: '',  // 3D scene part coloring; 'speed', 'torque', 'power'
    },
  },
  getters: {
    getViewerTab: (state) => (name) => {
      return state.viewer.closableTabs.find(tab => tab.name === name);
    },
  },
  mutations: {
    // NEW MODEL REQUEST
    SET_SKIP_ROUTE_CONFIRM(state, val) {
      state.skipRouteConfirm = val
    },

    // DIALOGS & CONTEXT MENUS
    TOGGLE_DIALOG_VISIBILITY(state, componentName) {
      // componentName: String of dialog component name.
      state.visible.dialog[componentName] = !state.visible.dialog[componentName];
    },
    SHOW_CONTEXTMENU(state, type) {
      // Show context menu based on type.

      const componentName = CONTEXT_MENUS_MAP[type];
      if (componentName) state.visible.contextmenu[componentName] = true;
    },
    HIDE_CONTEXTMENU(state) {
      // Hide all context menus.

      state.visible.contextmenu = {...CONTEXT_MENUS};
    },

    // CLOSABLE TABS
    SET_ACTIVE_VIEWER_TAB(state, tabName) {
      // Set the name of the current tab to keep track of which tab is active.  Does not change view.

      // if tab changed to 3D viewer, trigger render
      if (tabName === '1' && tabName !== state.viewer.activeTabName) {
        EventBus.$emit('render');
      }

      state.viewer.activeTabName = tabName;
    },
    SET_VIEWER_TAB_TITLE(state, title) {
      // Set the title of the current tab.

      const activeTab = state.viewer.closableTabs.find(tab => tab.name === state.viewer.activeTabName);
      activeTab.title = title;
    },
    APPEND_VIEWER_TAB(state, newTabObject) {
      // Do not call directly, see action appendViewerTab.
      state.viewer.closableTabs.push(newTabObject);
    },
    REMOVE_VIEWER_TAB(state, tabName) {
      // Do not call directly, see Vuex action removeViewerTab.
      state.viewer.closableTabs = state.viewer.closableTabs.filter(tab => tab.name !== tabName);
    },
    TOGGLE_VIEWER_TAB(state) {
      // Toggle active tab state to trigger update of computed status message.
      // Intended for triggering a status message update when the active tab is unchanged.

      const activeTabName = state.viewer.activeTabName;
      state.viewer.activeTabName = '';
      state.viewer.activeTabName = activeTabName;
    },

    // LOADING STATUS
    LOADING_MODEL(state, val) {
      state.loading = val;
    },
    LOADING_VIEWER(state, val) {
      state.viewer.loading = val;
    },
    SET_STATUS(state, {message, loading=true, tabName=''}) {
      // Set status for the specified or current viewer tab.
      if (!tabName) tabName = state.viewer.activeTabName;
      state.status.message[tabName] = message;
      state.status.loading = loading;
    },
    CLEAR_STATUS(state, tabName='') {
      // Clear status for the specified or current viewer tab.
      if (!tabName) tabName = state.viewer.activeTabName;
      state.status.message[tabName] = '';
      state.status.loading = false;
    },

    // ASSIGNMENTS
    SELECT_BASIC_RACK(state) {
      state.assign.basicRack = true;
      state.visible.dialog.DialogLibBasicRack = !state.visible.dialog.DialogLibBasicRack;
    },
    ASSIGN_BASIC_RACK(state, basicRack) {
      EventBus.$emit('assign-basic-rack', basicRack);
      state.assign.basicRack = false;
    },
    SELECT_HOB(state) {
      state.assign.hob = true;
      state.visible.dialog.DialogLibHob = !state.visible.dialog.DialogLibHob;
    },
    ASSIGN_HOB(state, hob) {
      EventBus.$emit('assign-hob', hob);
      state.assign.hob = false;
    },
    SELECT_SHAPER(state) {
      state.assign.shaper = true;
      state.visible.dialog.DialogLibShaper = !state.visible.dialog.DialogLibShaper;
    },
    ASSIGN_SHAPER(state, shaper) {
      EventBus.$emit('assign-shaper', shaper);
      state.assign.shaper = false;
    },

    // SELECTIONS
    SET_SELECTED_EVT(state, evt) {
      state.selected.evt = evt;
    },
    SET_SELECTED_PARTS(state, parts) {
      // clear any selected connections
      state.selected.connection = null;
      state.selected.connections = [];

      // mutate selected parts
      state.selected.parts = parts;

      // single part selection state
      if (parts.length === 1) {
        state.selected.part = parts[0];
      } else {
        state.selected.part = null;
      }
    },
    SET_SELECTED_CONNECTIONS(state, connections) {
      // clear any selected parts
      state.selected.part = null;
      state.selected.parts = [];

      // mutate selected connections
      state.selected.connections = connections;

      // single conn selection state
      if (connections.length === 1) {
        state.selected.connection = connections[0]
      } else {
        state.selected.connection = null;
      }
    },
    SET_SELECTED_CONFIG(state, config) {
      state.selected.config = config;
    },
    SET_SELECTED_DUTY_CYCLE(state, dutyCycle) {
      state.selected.dutyCycle = dutyCycle;
    },
    SET_SELECTED_LOAD_CASE(state, loadCase) {
      state.selected.loadCase = loadCase;
    },
    CLEAR_SELECTED(state) {
      state.selected.evt = null;
      state.selected.part = null;
      state.selected.parts = [];
      state.selected.connection = null;
      state.selected.connections = [];
      state.selected.config = null;
      state.selected.dutyCycle = null;
      state.selected.loadCase = null;
    },

    // MISC
    SET_KEYDOWN(state, {key, val}) {
      state.keydown[key] = val;
    },
    SET_EDIT_PART(state, part) {
      state.editPart = part;
    },
    SET_EDIT_CONFIG(state, config) {
      state.editConfig = config;
    },
    SET_EXPORT_PARTS(state, parts) {
      state.exportParts = parts;
    },
    SET_EXPORT_CONNS(state, conns) {
      state.exportConns = conns;
    },
    SET_EXPORT_DUTY_CYCLE(state, dutyCycle) {
      state.exportDutyCycle = dutyCycle;
    },
    SET_EXPORT_LOAD_CASES(state, loadCases) {
      state.exportLoadCases = loadCases;
    },
    SET_SIZING(state, diameters) {
      state.size.diameters = diameters;
      state.size.diameterMin = diameters.length > 0 ? Math.min(...diameters) : 1;
      state.size.crossHairLength = 0.25 * state.size.diameterMin;
    },

    // ANALYZE
    SET_LOAD_CASE(state, loadCase) {
      // Set load case for review, e.g. to animate.
      // Data structure per LoadCase schema, loadCase.powerflow: [{part, connection, speed, torque, __typename}, ...]

      state.analyze.loadCase = {
        ...loadCase,
        powerflow: !loadCase.powerflow ? null : handlers.model.mapResultsPowerflow(loadCase.powerflow),
      };
    },
    SET_ANIMATE(state, animate) {
      state.analyze.animate = animate;
    },
    SET_PLAYBACK(state, playbackSpeed) {
      state.analyze.playbackSpeed = playbackSpeed;
    },
    SET_POWERFLOW_COLOR(state, color) {
      state.analyze.color = color;
    },
    CLEAR_LOAD_CASE(state) {
      state.analyze.loadCase = null;
    },
  },
  actions: {
    showContextMenu({commit}, {type, event}) {
      // hide any existing contextmenu
      commit('HIDE_CONTEXTMENU');

      // timeout required to hide menu before showing new menu
      setTimeout(() => {
        commit('SHOW_CONTEXTMENU', type);
        commit('SET_SELECTED_EVT', event);
      }, 50)
    },
    appendViewerTab({state, commit}, newTabObject) {
      // Reference Viewer.vue to see the tab object properties required to create a new closable tab.
      // Tab object must contain a unique name

      // assign unique name to tab; tracking tab names numerically
      let newTabName = ++state.viewer.tabIndex + '';
      newTabObject.name = newTabName;

      // append new tab and set as active
      commit('APPEND_VIEWER_TAB', newTabObject);
      commit('SET_ACTIVE_VIEWER_TAB', newTabName)
    },
    removeViewerTab({state, commit}, tabName) {
      const tabs = state.viewer.closableTabs;
      let activeName = state.viewer.activeTabName;

      // assess which tab will become active
      if (activeName === tabName) {
        tabs.forEach((tab, index) => {
          if (tab.name === tabName) {
            let nextTab = tabs[index + 1] || tabs[index - 1];
            if (nextTab) {
              activeName = nextTab.name;
            } else {
              activeName = '1';
            }
          }
        });
      }

      // set the active tab and remove specified tab
      commit('SET_ACTIVE_VIEWER_TAB', activeName);
      commit('REMOVE_VIEWER_TAB', tabName);
    },
    setLoadCase({commit, dispatch}, loadCase) {
      // Set load case for review, and if results exist, then make API calls to validate / update results.

      commit('SET_LOAD_CASE', loadCase);

      // check if powerflow expired
      if (loadCase.powerflow) {
        dispatch('hasPowerflow', loadCase.Id).then(hasPowerflow => {
          // if powerflow expired, then rerun
          if (!hasPowerflow) {
            dispatch('runPowerflow', {
              Id: loadCase.Id,
              typ: requests.model.RunCaseType.LOAD_CASE
            }).then(() => {/*do nothing*/})
          }
        });
      }
    },
    resetLoadCase({state, getters, commit}) {
      if (state.analyze.loadCase) {
        commit('SET_LOAD_CASE', getters.getLoadCase(state.analyze.loadCase.Id));
      }
    },
    clearLoadCase({commit}) {
      commit('SET_ANIMATE', false);
      commit('CLEAR_LOAD_CASE');
    },
    clearUI({commit}) {
      commit('CLEAR_SELECTED');
      commit('HIDE_CONTEXTMENU');
      commit('SET_ANIMATE', false);
    },
  },
};
